/**
 * Diálogo de cambio de clave DESDE LA PANTALLA DE LOGIN (sin sesión iniciada).
 *
 * Seguridad: como no hay identidad probada, EXIGE la clave actual válida
 * (la personal del usuario o, en modo PRUEBAS, la clave-maestra) antes de
 * permitir fijar la nueva. Sin esa verificación, cualquiera podría cambiar
 * la clave de otro → toma de cuenta. El email debe corresponder a un usuario
 * activo conocido.
 */

import { useState } from "react";
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Field,
  Input,
  Text,
  MessageBar,
  MessageBarBody,
} from "@fluentui/react-components";
import {
  establecerClave,
  verificarClave,
  leerModoAcceso,
  CLAVE_MAESTRA_PRUEBAS,
} from "./credenciales";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Emails de usuarios activos conocidos (en minúscula), para validar el email. */
  knownEmails: string[];
}

export function CambiarClaveLoginDialog({ open, onOpenChange, knownEmails }: Props) {
  const [email, setEmail] = useState("");
  const [claveActual, setClaveActual] = useState("");
  const [nueva, setNueva] = useState("");
  const [confirma, setConfirma] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const cerrar = () => {
    setEmail("");
    setClaveActual("");
    setNueva("");
    setConfirma("");
    setError(null);
    setOk(false);
    onOpenChange(false);
  };

  const onGuardar = async (): Promise<void> => {
    setError(null);
    const correo = email.trim().toLowerCase();
    if (!knownEmails.includes(correo)) {
      setError("Ese correo no corresponde a un usuario activo del PMIS.");
      return;
    }
    if (nueva.length < 4) {
      setError("La clave nueva debe tener al menos 4 caracteres.");
      return;
    }
    if (nueva !== confirma) {
      setError("La clave nueva y su confirmación no coinciden.");
      return;
    }
    setEnviando(true);
    try {
      // Verificar identidad con la clave ACTUAL (personal o maestra en PRUEBAS).
      const modo = leerModoAcceso();
      let identidadOk = false;
      if (modo === "clave-maestra" && claveActual === CLAVE_MAESTRA_PRUEBAS) {
        identidadOk = true;
      } else {
        identidadOk = (await verificarClave(correo, claveActual)) !== "nope";
      }
      if (!identidadOk) {
        setError("La clave actual no es correcta. No se cambió nada.");
        return;
      }
      await establecerClave(correo, nueva, correo);
      setOk(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo cambiar la clave.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => (d.open ? onOpenChange(true) : cerrar())}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Cambiar mi clave</DialogTitle>
          <DialogContent>
            {ok ? (
              <MessageBar intent="success">
                <MessageBarBody>
                  Clave actualizada. Ya puede ingresar con su nueva clave. Guárdela:
                  no se muestra en ningún lado.
                </MessageBarBody>
              </MessageBar>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <Text size={200}>
                  Verifique su identidad con su clave actual y defina la nueva.
                  Si no recuerda su clave actual, pídale al administrador que la
                  restablezca.
                </Text>
                <Field label="Su correo">
                  <Input
                    type="email"
                    value={email}
                    onChange={(_, d) => setEmail(d.value)}
                    autoComplete="username"
                    placeholder="su.correo@empresa.com"
                  />
                </Field>
                <Field label="Clave actual">
                  <Input
                    type="password"
                    value={claveActual}
                    onChange={(_, d) => setClaveActual(d.value)}
                    autoComplete="current-password"
                    placeholder="Su clave actual"
                  />
                </Field>
                <Field label="Nueva clave (mínimo 4 caracteres)">
                  <Input
                    type="password"
                    value={nueva}
                    onChange={(_, d) => setNueva(d.value)}
                    autoComplete="new-password"
                    placeholder="Su nueva clave"
                  />
                </Field>
                <Field label="Confirmar nueva clave">
                  <Input
                    type="password"
                    value={confirma}
                    onChange={(_, d) => setConfirma(d.value)}
                    autoComplete="new-password"
                    placeholder="Repita la nueva clave"
                  />
                </Field>
                {error && (
                  <MessageBar intent="error">
                    <MessageBarBody>{error}</MessageBarBody>
                  </MessageBar>
                )}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={cerrar} disabled={enviando}>
              {ok ? "Cerrar" : "Cancelar"}
            </Button>
            {!ok && (
              <Button appearance="primary" onClick={() => void onGuardar()} disabled={enviando}>
                {enviando ? "Verificando…" : "Cambiar clave"}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
